import { QueryCache, QueryClient } from '@tanstack/react-query';
import { isAccessRefused } from '../domain/access-refusal';

/**
 * One QueryClient factory for every platform. Defaults tuned for a routine app
 * used a few minutes a day: data is fresh for a minute, kept for a day so the
 * home screen renders instantly from cache. The mobile app also saves the cache to a file
 * (`persist.ts`); the web app does not.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    // What a refused request had loaded is no longer the person's to see, even if a later
    // retry fails for another reason.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isAccessRefused(error)) query.setState({ data: undefined, dataUpdatedAt: 0 });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 24 * 60 * 60_000,
        // A refusal is final: a retry failing offline would replace it as the query's error.
        retry: (failures, error) => failures < 2 && !isAccessRefused(error),
        refetchOnWindowFocus: false,
        // Run and fail rather than pause while the device reports offline: callers act on the
        // failure (cached data stays on screen, a finished exercise goes to the outbox).
        networkMode: 'always',
      },
      // As queries: a mutation that should wait for the network says so itself.
      mutations: { retry: 0, networkMode: 'always' },
    },
  });
}
